import {
  Map,
  AdvancedMarker,
  APIProvider,
  Pin,
  MapMouseEvent,
} from '@vis.gl/react-google-maps';
import { Sheet, SheetRef } from 'react-modal-sheet';
import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import { Button, Spinner, Text } from 'ui_2';
import { Polyline } from './Polyline';
import { useGameStore } from '@src/data/useGameStore';
import { CoordinatePair } from '@src/data/types';
import { MYSTERY_CITY_AUDIO_BASE_URL, roundPhrase } from '@src/data/utils';
import useWindowDimensions from '@puzzlehq/frontend-utils/src/hooks/useWindowDimensions';

const ticketRewardSound = `${MYSTERY_CITY_AUDIO_BASE_URL}/ticketReward.mp3`;
const noTicketRewardSound = `${MYSTERY_CITY_AUDIO_BASE_URL}/noTicketReward.mp3`;

// Add type for the ref
export interface InteractiveFlatMapRef {
  setSubmit: (value: boolean) => void;
}

export const InteractiveFlatMap = forwardRef<
  InteractiveFlatMapRef,
  {
    nextRoundCountdown?: number;
    onSubmit: (submitted: boolean) => void;
    onGameFinish: () => void;
  }
>(({ nextRoundCountdown, onSubmit, onGameFinish }, ref) => {
  const [snapIndex, setSnapIndex] = useState<0 | 1>(1);
  const sheetRef = useRef<SheetRef>();

  const snapTo = (i: 0 | 1) => {
    setSnapIndex(i);
    sheetRef.current?.snapTo(i);
  };

  const [submit, setSubmit] = useState(false);

  const [round, rounds, storeGuess, submitRound] = useGameStore((state) => [
    state.round,
    state.rounds,
    state.storeGuess,
    state.submitRound,
  ]);

  // Expose setSubmit method via ref
  useImperativeHandle(ref, () => ({
    setSubmit: (value: boolean) => {
      snapTo(0);
      setSubmit(value);
      onSubmit(value);
    },
  }));

  const handleClick = (event: MapMouseEvent) => {
    if (!submit && event && event.detail.latLng) {
      storeGuess(round, event.detail.latLng);
    }
  };

  function calculateDistance() {
    setSubmit(true);
    onSubmit(true);
    submitRound();
  }

  const { width } = useWindowDimensions();
  const isLgScreen = width >= 1024;

  return (
    <>
      {/* Mobile view with Sheet */}
      {!isLgScreen && (
        <div className='absolute bottom-0 left-0 z-40 w-full lg:hidden '>
          <Sheet
            isOpen={true}
            onClose={() => snapTo(1)}
            snapPoints={[0.55, 46.13]}
            initialSnap={snapIndex}
            disableDrag={true}
            style={{
              background: 'transparent',
              zIndex: 30,
            }}
            className='lg:hidden'
          >
            <Sheet.Container
              style={{ background: 'transparent' }}
              onTap={(e) => {
                if (snapIndex > 0) {
                  snapTo(0);
                  e.stopPropagation();
                  e.stopImmediatePropagation();
                }
              }}
            >
              <Sheet.Content>
                <div
                  className={`z-[100] flex h-[52px] w-full ${
                    snapIndex === 0 ? '' : 'cursor-pointer'
                  } mx-auto items-center justify-center rounded-t-xl bg-black`}
                  onClick={() => {
                    if (snapIndex === 1) snapTo(0);
                  }}
                >
                  <Text family='space' color='aqua'>
                    {nextRoundCountdown !== undefined
                      ? round === rounds - 1
                        ? `Game ends in ${nextRoundCountdown.toFixed(0)}s`
                        : `Next round in ${nextRoundCountdown.toFixed(0)}s`
                      : 'Guess the city'}
                  </Text>
                </div>
                <MapContent
                  handleClick={(e) =>
                    snapIndex === 0 ? handleClick(e) : undefined
                  }
                  submit={submit}
                  calculateDistance={calculateDistance}
                  setSubmit={setSubmit}
                  onGameFinish={onGameFinish}
                  snapTo={snapTo}
                  onSubmit={onSubmit}
                />
              </Sheet.Content>
            </Sheet.Container>
            {snapIndex === 0 ? (
              <Sheet.Backdrop onTap={() => snapTo(1)} />
            ) : (
              <></>
            )}
          </Sheet>
        </div>
      )}

      {/* Desktop view */}
      {isLgScreen && (
        <div className='z-20 hidden lg:absolute lg:bottom-4 lg:right-4 lg:block lg:h-[647px] lg:max-h-[65%] lg:w-[375px] lg:overflow-hidden lg:rounded-lg'>
          <div className='h-full w-full'>
            <div className='flex h-[52px] w-[100%] items-center justify-center rounded-t-xl bg-black'>
              <Text family='space' color='aqua'>
                {nextRoundCountdown !== undefined
                  ? round === rounds - 1
                    ? `Game ends in ${nextRoundCountdown.toFixed(0)}s`
                    : `Next round in ${nextRoundCountdown.toFixed(0)}s`
                  : 'Guess the city'}
              </Text>
            </div>
            <MapContent
              handleClick={handleClick}
              submit={submit}
              calculateDistance={calculateDistance}
              setSubmit={setSubmit}
              onGameFinish={onGameFinish}
              snapTo={snapTo}
              onSubmit={onSubmit}
            />
          </div>
        </div>
      )}
    </>
  );
});

// Extracted map content to avoid duplication
interface MapContentProps {
  handleClick: (event: MapMouseEvent) => void;
  submit: boolean;
  calculateDistance: () => void;
  setSubmit: (submit: false) => void;
  onGameFinish: () => void;
  snapTo: (i: 0 | 1) => void;
  onSubmit: (submitted: boolean) => void;
}

const MapContent = ({
  handleClick,
  submit,
  calculateDistance,
  setSubmit,
  onGameFinish,
  snapTo,
  onSubmit,
}: MapContentProps) => {
  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
  const [
    guesses,
    answers,
    distances,
    round,
    rounds,
    roundScores,
    submitStatus,
    incrementRound,
    isMuted,
  ] = useGameStore((state) => [
    state.guesses,
    state.answers,
    state.distances,
    state.round,
    state.rounds,
    state.roundScores,
    state.submitStatus,
    state.incrementRound,
    state.isMuted,
  ]);

  const guessPosition: CoordinatePair | undefined = guesses[round];
  const answerPosition = answers[round];

  useEffect(() => {
    if (submit && mapRef && guessPosition) {
      const bounds = new google.maps.LatLngBounds();
      bounds.extend(answerPosition);
      bounds.extend(guessPosition);
      mapRef.fitBounds(bounds);
    } else if (submit && mapRef) {
      mapRef.setCenter(answerPosition);
    }
  }, [submit, mapRef, answerPosition, guessPosition]);

  useEffect(() => {
    if (mapRef) {
      mapRef.setZoom(DEFAULT_ZOOM);
      mapRef.setCenter(DEFAULT_CENTER);
      snapTo(1);
    }
  }, [round]);

  useEffect(() => {
    console.log('distances', distances, round);
  }, [distances]);

  const DEFAULT_ZOOM = 0.5;
  const DEFAULT_CENTER = { lat: 22.54992, lng: 0 };

  useEffect(() => {
    if (submit && distances[round] !== undefined) {
      const currentRoundScore = roundScores[round] ?? 0;
      if (currentRoundScore === 0 && !isMuted) {
        const audio = new Audio(noTicketRewardSound);
        void audio.play();
      } else if (currentRoundScore > 0 && !isMuted) {
        void playTicketSound(currentRoundScore);
      }
    }
  }, [distances, round, submit]);

  const playTicketSound = async (times: number) => {
    if (isMuted) return;
    const audio = new Audio(ticketRewardSound);
    for (let i = 0; i < times; i++) {
      await new Promise((resolve, reject) => {
        audio.onended = resolve;
        audio.onerror = reject;
        const playPromise = audio.play();
        if (playPromise) {
          playPromise.catch((error) => reject(error));
        }
      });
    }
  };

  return (
    <div className='h-full' onClick={(e) => e.stopPropagation()}>
      <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY!}>
        <Map
          onIdle={(event) => setMapRef(event.map)}
          className='h-full w-full'
          defaultZoom={0.5}
          defaultCenter={{ lat: 22.54992, lng: 0 }}
          gestureHandling={'greedy'}
          disableDefaultUI={true}
          mapId='ad080886f7fe1807'
          colorScheme='DARK'
          onClick={(event) => handleClick(event)}
          mapTypeId='roadmap'
          streetViewControl={false}
          scaleControl={false}
          mapTypeControl={false}
          panControl={false}
          zoomControl={false}
          rotateControl={false}
          cameraControl={false}
          clickableIcons={false}
          fullscreenControl={false}
          scrollwheel={true}
          minZoom={2}
          maxZoom={15}
          restriction={{
            latLngBounds: {
              north: 85,
              south: -85,
              west: -180,
              east: 180,
            },
            strictBounds: false,
          }}
        >
          {submit &&
            guessPosition?.lat &&
            guessPosition.lat !== 0 &&
            guessPosition?.lng &&
            guessPosition.lng !== 0 && (
              <Polyline
                strokeColor='transparent'
                icons={[
                  {
                    icon: {
                      path: 'M 0,-1 0,1',
                      strokeOpacity: 1,
                      scale: 4,
                      strokeWeight: 4,
                      strokeColor: '#33FF00',
                    },
                    offset: '50px',
                    repeat: '20px',
                  },
                ]}
                geodesic
                path={[
                  answerPosition,
                  { lat: guessPosition.lat, lng: guessPosition.lng },
                ]}
              />
            )}
        </Map>
        {guessPosition?.lat &&
          guessPosition.lat !== 0 &&
          guessPosition?.lng &&
          guessPosition.lng !== 0 && (
            <AdvancedMarker
              position={{ lat: guessPosition.lat, lng: guessPosition.lng }}
            >
              <Pin glyphColor='aqua' borderColor='aqua' background='aqua' />
            </AdvancedMarker>
          )}
        {submit && (
          <AdvancedMarker position={answerPosition}>
            <Pin
              glyphColor='#33FF00'
              borderColor='#33FF00'
              background='#33FF00'
            />
          </AdvancedMarker>
        )}
        {/*  if theres not a distance at round 0, don't show*/}

        {distances[round] && distances[round] !== -1 && (
          <div className='absolute bottom-28 left-1/2 flex h-[91px] w-[250px] -translate-x-1/2 transform flex-col items-center justify-center rounded-[20px] border border-[#00F0FF] bg-black bg-gradient-to-b from-[rgba(96,186,225,0.2)] to-[rgba(96,186,225,0.2)] p-2'>
            <Text family='space' font='md' className='text-[#3CFF00]'>
              {roundPhrase(roundScores[round])}
            </Text>
            <div className='flex flex-col items-center justify-center gap-2'>
              <Text family='space' font='md' className='text-[#3CFF00]'>
                You are {distances[round]} km away
              </Text>
              <Text family='space' font='md' color='aqua'>
                You win {roundScores[round]}/5 tickets
              </Text>
            </div>
          </div>
        )}
        {distances[round] && distances[round] === -1 && (
          <div className='absolute bottom-28 left-1/2 flex h-[91px] w-[250px] -translate-x-1/2 transform flex-col items-center justify-center rounded-[20px] border border-[#00F0FF] bg-black bg-gradient-to-b from-[rgba(96,186,225,0.2)] to-[rgba(96,186,225,0.2)] p-2'>
            <Text family='space' font='md' className='text-[#3CFF00]'>
              Try again
            </Text>
          </div>
        )}
        {submit && guessPosition?.lat === 0 && (
          <div className='absolute bottom-28 left-1/2 flex h-[91px] w-[334px] -translate-x-1/2 transform flex-col items-center justify-center rounded-[20px] border border-[#00F0FF] bg-black bg-gradient-to-b from-[rgba(96,186,225,0.2)] to-[rgba(96,186,225,0.2)] p-2'>
            <Text family='space' font='md' className='text-[#3CFF00]'>
              Try again
            </Text>
          </div>
        )}
        {!submit && (
          <div className='absolute bottom-10 left-1/2 -translate-x-1/2 transform'>
            <div className='absolute left-0 top-0 h-full w-full rounded-full bg-black'></div>{' '}
            {/* Background div */}
            <Button
              variant='mysteryCityPrimary'
              className='relative h-full w-full'
              onClick={() => {
                calculateDistance();
              }}
              disabled={!guessPosition}
            >
              Submit
            </Button>
          </div>
        )}
        {submit && (
          <div className='absolute bottom-10 left-1/2 -translate-x-1/2 transform'>
            <div className='absolute left-0 top-0 h-full w-full rounded-full bg-black'></div>{' '}
            {/* Background div */}
            <Button
              variant='mysteryCityPrimary'
              className='relative h-full w-full items-center gap-2'
              onClick={() => {
                if (round === rounds - 1) {
                  onGameFinish();
                } else {
                  setSubmit(false);
                  onSubmit(false);
                  incrementRound();
                  snapTo(1);
                  if (mapRef) {
                    mapRef.setZoom(DEFAULT_ZOOM);
                    mapRef.setCenter(DEFAULT_CENTER);
                  }
                }
              }}
              disabled={submitStatus === 'loading' && round === rounds - 1}
            >
              {round === rounds - 1 ? 'Claim Prize' : 'Next Round'}
              {round === rounds - 1 && submitStatus === 'loading' && (
                <Spinner />
              )}
            </Button>
          </div>
        )}
      </APIProvider>
    </div>
  );
};

export default InteractiveFlatMap;
